/**
 * Safe wrappers around localStorage that handle the 3 most common
 * failure modes that crash the app:
 *
 *   1. localStorage contains the literal string "undefined" or "null"
 *      (set by buggy code that does `localStorage.setItem(k, undefined)`).
 *   2. localStorage contains corrupt JSON (e.g. user manually edited
 *      DevTools, an old build wrote a different shape, partial write).
 *   3. localStorage is unavailable (SSR, sandboxed iframe, file://).
 *
 * The previous code used `JSON.parse(localStorage.getItem(...))` directly
 * across 48+ files. Any of the above would throw `SyntaxError: Unexpected
 * token u in JSON at position 0` (or similar) and unmount the app with
 * a blank screen. The helpers below turn every failure mode into a
 * well-defined return value (default) so the calling code can keep
 * working with sane defaults.
 *
 * Two flavours:
 *   - `safeGetString` / `safeSetString` — raw string values
 *   - `safeGetJSON` / `safeSetJSON`    — JSON-encoded values
 *
 * Plus domain-specific helpers for the keys that this app uses heavily
 * (Presensi-Auth-Key, Presensi-Auth-User, Presensi-Company-Data, ...).
 * These are the 48+ duplicates the original `02-blank-screen-prevention.md`
 * audit called out — see the bottom of this file for the full list.
 */

const isStorageAvailable = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    const probe = '__presensi_storage_probe__';
    window.localStorage.setItem(probe, probe);
    window.localStorage.removeItem(probe);
    return true;
  } catch (e) {
    return false;
  }
};

const STORAGE_OK = isStorageAvailable();

/**
 * Read a raw string value from localStorage.
 * Returns `defaultValue` (or `null`) if the key is missing, the value
 * is the string "undefined"/"null", or storage is unavailable.
 */
export const safeGetString = (key, defaultValue = null) => {
  if (!STORAGE_OK) return defaultValue;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null || raw === undefined || raw === 'undefined' || raw === 'null') {
      return defaultValue;
    }
    return raw;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[safeStorage] Failed to read "${key}":`, e);
    return defaultValue;
  }
};

/**
 * Write a raw string value to localStorage. Returns `true` on success,
 * `false` on failure (quota exceeded, storage disabled, etc.). Never
 * throws.
 */
export const safeSetString = (key, value) => {
  if (!STORAGE_OK) return false;
  try {
    window.localStorage.setItem(key, String(value));
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[safeStorage] Failed to set "${key}":`, e);
    return false;
  }
};

/**
 * Read a JSON-encoded value from localStorage. Returns the parsed
 * value, or `defaultValue` if any of the following happens:
 * - the key is missing
 * - the stored value is "undefined" / "null" / empty
 * - the stored value is not valid JSON
 * - the parsed value's type doesn't match `expectedType` (when provided)
 * - storage is unavailable
 */
export const safeGetJSON = (key, defaultValue = null, expectedType = null) => {
  if (!STORAGE_OK) return defaultValue;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null || raw === undefined || raw === 'undefined' || raw === 'null' || raw === '') {
      return defaultValue;
    }
    const parsed = JSON.parse(raw);
    if (expectedType) {
      // expectedType is a string: 'object' | 'array' | 'string' | 'number' | 'boolean'
      const actual = Array.isArray(parsed) ? 'array' : typeof parsed;
      if (actual !== expectedType) return defaultValue;
    }
    return parsed;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[safeStorage] Failed to parse "${key}":`, e);
    return defaultValue;
  }
};

/**
 * Write a JSON-encoded value to localStorage. Returns `true` on
 * success, `false` on failure. Never throws.
 */
export const safeSetJSON = (key, value) => {
  if (!STORAGE_OK) return false;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[safeStorage] Failed to set JSON "${key}":`, e);
    return false;
  }
};

/**
 * Remove a key from localStorage. No-op if the key doesn't exist.
 * Returns `true` on success, `false` on failure.
 */
export const safeRemove = (key) => {
  if (!STORAGE_OK) return false;
  try {
    window.localStorage.removeItem(key);
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[safeStorage] Failed to remove "${key}":`, e);
    return false;
  }
};

// ----------------------------------------------------------------------------
// Domain-specific helpers
// ----------------------------------------------------------------------------
//
// These wrap the generic helpers above for the localStorage keys that
// this app actually uses. Centralizing the key strings here means a
// rename (or a future migration to cookie-based auth) is a 1-file
// change instead of a 48-file find-and-replace.

const KEY = {
  AUTH:         'Presensi-Auth',
  AUTH_TOKEN:   'Presensi-Auth-Key',
  AUTH_USER:    'Presensi-Auth-User',
  COMPANY_DATA: 'Presensi-Company-Data',
  TOKEN_MESSAGE: 'token-message',
  DATA_MESSAGE:  'data-message-user',
};

/* -------------------- Auth helpers -------------------- */

export const getAuthFlag = () => safeGetString(KEY.AUTH) === 'true';

export const setAuthFlag = () => safeSetString(KEY.AUTH, true);

export const getAuthToken = () => safeGetString(KEY.AUTH_TOKEN);

/**
 * Get the auth token. Reads from `Presensi-Auth-Key`. Returns null if
 * missing, empty, or "undefined"/"null". Note: do NOT cache this — the
 * token can be cleared by logout at any time.
 */
export const setAuthToken = (token) => safeSetString(KEY.AUTH_TOKEN, token);

export const getAuthUser = () => safeGetJSON(KEY.AUTH_USER, null, 'object');

export const setAuthUser = (user) => safeSetJSON(KEY.AUTH_USER, user);

export const clearAuth = () => {
  safeRemove(KEY.AUTH);
  safeRemove(KEY.AUTH_TOKEN);
  safeRemove(KEY.AUTH_USER);
};

/* -------------------- Company / Filter helpers -------------------- */

/**
 * Get the company filter data (jabatan/divisi/lokasi/etc) for the
 * current user. Replaces the duplicated `JSON.parse(localStorage.getItem('Presensi-Company-Data'))`
 * pattern used 13+ times in list pages.
 */
export const getCompanyData = () => safeGetJSON(KEY.COMPANY_DATA, null, 'object');

export const setCompanyData = (data) => safeSetJSON(KEY.COMPANY_DATA, data);

export const clearCompanyData = () => safeRemove(KEY.COMPANY_DATA);

/* -------------------- Chat helpers -------------------- */

export const getTokenMessage = () => safeGetString(KEY.TOKEN_MESSAGE);

export const setTokenMessage = (token) => safeSetString(KEY.TOKEN_MESSAGE, token);

export const getDataMessage = () => safeGetJSON(KEY.DATA_MESSAGE, null, 'object');

export const setDataMessage = (data) => safeSetJSON(KEY.DATA_MESSAGE, data);

// Backwards-compatible default export (an object with all helpers).
const safeStorage = {
  isStorageAvailable: () => STORAGE_OK,
  safeGetString,
  safeSetString,
  safeGetJSON,
  safeSetJSON,
  safeRemove,
  getAuthFlag,
  setAuthFlag,
  getAuthToken,
  setAuthToken,
  getAuthUser,
  setAuthUser,
  clearAuth,
  getCompanyData,
  setCompanyData,
  clearCompanyData,
  getTokenMessage,
  setTokenMessage,
  getDataMessage,
  setDataMessage,
  KEYS: KEY,
};

export default safeStorage;
